import { getSerwist } from 'virtual:serwist';
import { isEmpty, isNil } from 'lodash-es';
import { filter, from, fromEvent, interval, type Observable, Subject, Subscription, share, switchMap } from 'rxjs';
import { z } from 'zod';
import { APP_VERSION_HASH, BUILD_EMBED_TYPE, BUILD_ENV, IS_DEV } from '@/utils/constants';
import { fetcher } from '@/utils/fetcher';
import { createLogger } from '@/utils/logger';

export function createServiceWorkerManager(): ServiceWorkerManager {
  return new ServiceWorkerManager();
}

export default class ServiceWorkerManager {
  readonly #logger = createLogger('Service Worker');
  #installPrompt: BeforeInstallPromptEvent | null = null;
  #isAppInstalled = false;
  #event$ = new Subject<ServiceWorkerEvent>();
  #subscription$$ = new Subscription();

  constructor() {
    this.initialize().catch((error) => {
      this.#logger.error(error);
    });
  }

  dispose(): void {
    this.#event$.unsubscribe();
    this.#subscription$$.unsubscribe();

    this.#event$ = new Subject<ServiceWorkerEvent>();
    this.#subscription$$ = new Subscription();
  }

  async initialize(): Promise<void> {
    await this.#promptAppInstall();
    await this.#loadServiceWorker();
  }

  async triggerAppInstall(): Promise<void> {
    if (this.#isAppInstalled) throw 'INSTALL_PROMPT_NOT_AVAILABLE';

    await this.#installPrompt?.prompt();
  }

  async dismissAppInstall(): Promise<void> {
    this.#installPrompt = null;
  }

  async triggerServiceWorkerUpdate(): Promise<void> {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();

      for (const registration of registrations) {
        await registration.update();

        if (registration.installing) {
          await this.#waitForServiceWorkerState(registration.installing, 'activated');
        }

        if (registration.waiting) {
          await this.#skipWaiting(registration.waiting);
          await this.#waitForServiceWorkerState(registration.waiting, 'activated');
        }
      }
    } catch (error) {
      this.#logger.error('Failed to update service worker:', error);
      throw new Error('Failed to update service worker');
    }
  }

  async checkVersionUpdate() {
    const version = await this.#getNewVersionName();
    const key = getVersionKey(version);

    return {
      ...version,
      key,
      hasUpdate: key !== APP_VERSION_HASH,
    };
  }

  get isAppInstalled(): boolean {
    return this.#isAppInstalled;
  }

  get event$(): Observable<ServiceWorkerEvent> {
    return this.#event$.asObservable().pipe(
      filter((event) => !isNil(event)),
      share(),
    );
  }

  async #promptAppInstall(): Promise<void> {
    if (IS_DEV || BUILD_ENV === 'embedded') return;
    if (!('getInstalledRelatedApps' in navigator) || typeof navigator.getInstalledRelatedApps !== 'function') return;

    const installedApp = await navigator
      .getInstalledRelatedApps()
      .then((apps) => apps.some((app) => app.platform === 'webapp'));
    if (installedApp) {
      this.#isAppInstalled = true;
      this.#installPrompt = null;
      return;
    }

    const subscription$$ = fromEvent(window, 'beforeinstallprompt')
      .pipe(filter((event): event is BeforeInstallPromptEvent => 'prompt' in event))
      .subscribe({
        next: (event) => {
          event.preventDefault();
          this.#installPrompt = event;
          this.#event$.next({ event: 'prompt-app-install' });
        },
      });

    this.#subscription$$.add(subscription$$);
  }

  async #loadServiceWorker(): Promise<void> {
    if (IS_DEV || BUILD_EMBED_TYPE === 'embed-native') return;
    if (!('serviceWorker' in navigator)) return;

    const serwist = await getSerwist();
    if (!serwist) return;

    serwist.addEventListener('installed', () => {
      this.#logger.info('installed');
    });

    const registration = await serwist.register();
    if (!registration) return;

    this.#logger.info('registered');

    if (registration.waiting && registration.waiting.state === 'installed') {
      registration.addEventListener('updatefound', () => {
        const installingWorker = registration.installing;
        if (!installingWorker) return;

        installingWorker.addEventListener('statechange', () => {
          if (installingWorker.state === 'installed' && navigator.serviceWorker.controller) {
            this.#logger.info('updates available');

            serwist.update();
          }
        });
      });
    }

    navigator.serviceWorker.addEventListener('controllerchange', () => {
      this.#logger.info('controller changed');

      this.#getNewVersionName()
        .then(({ version, key }) => {
          if (key === APP_VERSION_HASH) return;

          this.#event$.next({
            event: 'update-available',
            data: { version },
          });
        })
        .catch((error) => {
          this.#logger.warn(error);
        });
    });

    this.#pollServiceWorkerUpdate();
  }

  #pollServiceWorkerUpdate(): void {
    const subscription$$ = interval(30 * 60 * 1000)
      .pipe(
        switchMap(() => from(navigator.serviceWorker.getRegistrations())),
        filter((registrations) => !isEmpty(registrations)),
      )
      .subscribe({
        next: async (registrations) => {
          for (const registration of registrations) {
            if (registration.waiting && registration.waiting.state === 'installed') {
              await registration.update();
            }
          }
        },
      });

    this.#subscription$$.add(subscription$$);
  }

  async #getNewVersionName(): Promise<AppVersion & { key: string }> {
    const response = await fetcher<AppVersion>(
      '/version.json',
      { buster: Date.now() },
      {
        target: 'self',
        method: 'get',
        requireAuth: false,
        responseContentType: 'json',
        schema: appVersionSchema,
        cache: 'no-cache',
      },
    );

    return {
      ...response,
      key: getVersionKey(response),
    };
  }

  async #waitForServiceWorkerState(worker: ServiceWorker, targetState: ServiceWorkerState): Promise<void> {
    if (worker.state === targetState) return;

    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        worker.removeEventListener('statechange', onChange);
        reject(new Error('Service worker state change timeout'));
      }, 30000);

      const onChange = () => {
        if (worker.state === targetState) {
          clearTimeout(timeout);
          worker.removeEventListener('statechange', onChange);
          resolve();
        }
      };

      worker.addEventListener('statechange', onChange);
    });
  }

  async #skipWaiting(worker: ServiceWorker): Promise<void> {
    const channel = new MessageChannel();
    worker.postMessage({ type: 'SKIP_WAITING' }, [channel.port2]);

    return new Promise((resolve) => {
      channel.port1.onmessage = () => {
        channel.port1.close();
        resolve();
      };
    });
  }
}

function getVersionKey({ hash, timestamp, version }: AppVersion): string {
  return `v${version}-${hash?.slice(0, 6)}-${timestamp?.toString(36)}`;
}

const serviceWorkerEventScheme = z.discriminatedUnion('event', [
  z.object({
    event: z.literal('update-available'),
    data: z.object({
      version: z.string().nullish(),
    }),
  }),
  z.object({
    event: z.literal('prompt-app-install'),
  }),
]);

type ServiceWorkerEvent = z.infer<typeof serviceWorkerEventScheme>;

export const appVersionSchema = z.object({
  version: z.string().regex(/^\d+\.\d+\.\d+$/),
  hash: z.string().nullish(),
  timestamp: z.number().nullish(),
});

export type AppVersion = z.infer<typeof appVersionSchema>;
